import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { featuredSitters } from '../../mocks/sitters';

const getServicePrice = (service: string, base: number) => {
  if (service === '散歩') return base;
  if (service === '訪問ケア') return base + 5;
  if (service === 'お泊まり') return base + 25;
  if (service === 'タクシー送迎') return base + 8;
  return base;
};

export default function SitterDetailPage() {
  const { id } = useParams();
  const sitter = featuredSitters.find(s => s.id === Number(id));
  const [showChat, setShowChat] = useState(false);
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{text: string, sender: 'user' | 'sitter', time: string}>>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (!sitter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EEF4F7]">
        <div className="text-center px-4">
          <i className="ri-error-warning-line text-6xl text-[#D4E8EC] mb-4"></i>
          <p className="text-base sm:text-lg text-[#1B2A4A]/60">シッターが見つかりませんでした</p>
          <Link to="/sitters" className="mt-6 inline-block px-6 py-3 bg-[#81D8D0] text-white rounded-full hover:bg-[#1B2A4A] transition-colors whitespace-nowrap">
            シッター一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  const handleSendMessage = () => {
    if (!message.trim()) return;

    const forbiddenPatterns = [
      /電話/, /メール/, /ライン/, /line/i, /whatsapp/i,
      /\d{3}[-.\s]?\d{4}[-.\s]?\d{4}/, /@/, /\.com/, /\.jp/
    ];

    const hasForbiddenContent = forbiddenPatterns.some(pattern => pattern.test(message));

    if (hasForbiddenContent) {
      alert('⚠️ 外部連絡先の共有は禁止されています\n\nすべてのやり取りはPawBridge内で行ってください。');
      return;
    }

    const newMessage = {
      text: message,
      sender: 'user' as const,
      time: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages([...chatMessages, newMessage]);
    setMessage('');

    setTimeout(() => {
      const autoReply = {
        text: 'メッセージありがとうございます。できるだけ早くお返事いたします。',
        sender: 'sitter' as const,
        time: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages(prev => [...prev, autoReply]);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#EEF4F7] pb-24 lg:pb-16">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#81D8D0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-24 sm:h-28">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg" 
                alt="PawBridge" 
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain brightness-0"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8 lg:gap-10">
              <Link to="/about" className="relative text-sm font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                PawBridgeについて
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitters" className="relative text-sm font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                シッターを探す
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitter-registration" className="relative text-sm font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                シッター登録
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                to="/login"
                className="px-6 py-2.5 bg-white text-[#81D8D0] text-sm font-bold rounded-full hover:bg-[#1B2A4A] hover:text-white transition-all shadow-md hover:shadow-lg whitespace-nowrap"
              >
                ログイン
              </Link>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-1 animate-fadeIn border-t border-white/20 pt-3">
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">PawBridgeについて</Link>
              <Link to="/sitters" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">シッターを探す</Link>
              <Link to="/sitter-registration" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">シッター登録</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">ログイン</Link>
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)} className="block mx-4 mt-2 px-6 py-2.5 bg-white text-[#81D8D0] text-sm font-semibold rounded-full hover:bg-white/90 transition-colors text-center">予約する</Link>
            </div>
          )}
        </div>
      </nav>

      <div className="pt-28 sm:pt-32 pb-24 lg:pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Link to="/sitters" className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#1B2A4A]/60 hover:text-[#1B2A4A] mb-6 sm:mb-8 transition-colors">
            <i className="ri-arrow-left-line"></i>
            シッター一覧に戻る
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                <div className="h-64 sm:h-80 lg:h-96 relative">
                  <img
                    src="https://public.readdy.ai/ai/img_res/80cbbc4936546b746e46d6b83ff4ecb8.jpg"
                    alt="プロフィール画像"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full">
                    <i className="ri-time-line text-xs text-[#1B2A4A]/50"></i>
                    <span className="text-xs font-medium text-[#1B2A4A]/60">準備中</span>
                  </div>
                  {sitter.verified && (
                    <div className="absolute top-4 sm:top-6 left-4 sm:left-6 flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/95 backdrop-blur-sm text-[#1B2A4A] text-xs sm:text-sm font-semibold rounded-full border border-[#81D8D0]/40">
                      <div className="w-5 h-5 flex items-center justify-center bg-[#5BC0B8] rounded-full flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span>本人確認済み</span>
                    </div>
                  )}
                </div>

                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-3">{sitter.name}</h1>
                      <div className="flex items-center gap-2 text-sm sm:text-base text-[#1B2A4A]/60 mb-2">
                        <i className="ri-map-pin-line text-[#81D8D0]"></i>
                        {sitter.city}, {sitter.country}
                      </div>
                      <div className="flex items-center gap-1 text-sm sm:text-base">
                        <i className="ri-star-fill text-yellow-400"></i>
                        <span className="font-semibold text-[#1B2A4A]">{sitter.rating}</span>
                        <span className="text-[#1B2A4A]/60">({sitter.reviewCount}件のレビュー)</span>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-2xl sm:text-3xl font-bold text-[#81D8D0] mb-1">
                        €{sitter.pricePerVisit}
                      </div>
                      <div className="text-xs sm:text-sm text-[#1B2A4A]/60">1回あたり</div>
                    </div>
                  </div>

                  {/* 信頼バッジセクション */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {sitter.verified && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0FBF9] border border-[#C4E8E4] rounded-full">
                        <div className="w-4 h-4 flex items-center justify-center bg-[#5BC0B8] rounded-full flex-shrink-0">
                          <i className="ri-check-line text-white text-xs" style={{fontSize: '9px'}}></i>
                        </div>
                        <span className="text-xs font-semibold text-[#1B2A4A]">本人確認済み</span>
                        <span className="text-xs text-[#1B2A4A]/40">· Stripe Identity</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4F7] border border-[#D4E8EC] rounded-full">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-award-fill text-[#81D8D0] text-xs"></i>
                      </div>
                      <span className="text-xs font-semibold text-[#1B2A4A]">PawBridge認定</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4F7] border border-[#D4E8EC] rounded-full">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-[#81D8D0] text-xs"></i>
                      </div>
                      <span className="text-xs font-semibold text-[#1B2A4A]">安心保証対象</span>
                    </div>
                  </div>

                  <div className="border-t border-[#D4E8EC] pt-6">
                    <h2 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-4">自己紹介</h2>
                    <p className="text-sm sm:text-base text-[#1B2A4A]/80 leading-relaxed mb-6">{sitter.introduction}</p>
                    <p className="text-sm sm:text-base text-[#1B2A4A]/80 leading-relaxed">
                      ペットはただの動物ではなく、家族であり、子どものように大切な存在です。大切なペットを安心して預けていただけるよう、愛情を持ってお世話いたします。
                    </p>
                  </div>

                  <div className="border-t border-[#D4E8EC] pt-6 mt-6">
                    <h2 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-4">対応サービス</h2>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {sitter.services.map(service => (
                        <div key={service} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#EEF4F7] text-[#1B2A4A] text-xs sm:text-sm font-medium rounded-full border border-[#D4E8EC]">
                          {service}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-[#D4E8EC] pt-6 mt-6">
                    <h2 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-4">対応可能なペット</h2>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {sitter.pets.map(pet => (
                        <div key={pet} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#81D8D0]/10 text-[#1B2A4A] text-xs sm:text-sm font-medium rounded-full border border-[#81D8D0]/30">
                          {pet}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-[#D4E8EC] pt-6 mt-6">
                    <h2 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-4">経験</h2>
                    <p className="text-sm sm:text-base text-[#1B2A4A]/80">{sitter.experience}</p>
                  </div>

                  <div className="border-t border-[#D4E8EC] pt-6 mt-6">
                    <h2 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-4">レビュー</h2>
                    <div className="space-y-4">
                      {[
                        { initial: 'M', name: '松本さん', rating: '5.0', comment: 'とても丁寧にお世話していただきました。日本語で細かく報告してくださり、安心して預けることができました。' },
                        { initial: 'K', name: '木村さん', rating: '4.8', comment: 'うちの犬がとても懐いていました。また次回もお願いしたいです。' },
                      ].map((review, i) => (
                        <div key={i} className="p-3 sm:p-4 bg-[#EEF4F7] rounded-xl">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#81D8D0] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {review.initial}
                            </div>
                            <div>
                              <div className="text-sm sm:text-base font-semibold text-[#1B2A4A]">{review.name}</div>
                              <div className="flex items-center gap-1 text-xs sm:text-sm">
                                <i className="ri-star-fill text-yellow-400"></i>
                                <span className="text-[#1B2A4A]/60">{review.rating}</span>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs sm:text-sm text-[#1B2A4A]/80 leading-relaxed">{review.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Desktop Only */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-20 space-y-4">
                {/* Prominent Book Now CTA */}
                <Link
                  to={`/booking?sitterId=${sitter.id}`}
                  className="w-full px-6 py-4 bg-[#81D8D0] text-white text-base font-bold rounded-xl hover:bg-[#1B2A4A] transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-xl"></i>
                  今すぐ予約する
                </Link>

                {/* �安心保証バッジ */}
                <Link
                  to="/guarantee"
                  className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg hover:bg-[#EEF4F7] transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-check-fill text-sm text-[#81D8D0]"></i>
                  </div>
                  <span className="text-xs text-[#1B2A4A]/60 hover:text-[#1B2A4A] transition-colors">全ての予約に安心保証が含まれています</span>
                </Link>

                <button
                  onClick={() => setShowChat(!showChat)}
                  className="w-full px-6 py-3.5 bg-white border-2 border-[#1B2A4A] text-[#1B2A4A] text-base font-semibold rounded-xl hover:bg-[#1B2A4A] hover:text-white transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-message-3-line text-xl"></i>
                  メッセージを送る
                </button>

                {/* Pricing Table */}
                <div className="pt-4 border-t border-[#D4E8EC]">
                  <h3 className="text-sm font-bold text-[#1B2A4A] mb-3 flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-[#81D8D0]"></i>
                    料金表
                  </h3>
                  <div className="space-y-2">
                    {sitter.services.map(service => (
                      <div key={service} className="flex items-center justify-between py-2 border-b border-[#EEF4F7] last:border-0">
                        <span className="text-sm text-[#1B2A4A]/70">{service}</span>
                        <span className="text-sm font-bold text-[#1B2A4A]">
                          €{getServicePrice(service, sitter.pricePerVisit)}
                          <span className="text-xs font-normal text-[#1B2A4A]/40">
                            /{service === 'お泊まり' ? '泊' : '回'}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-2 border-t border-[#D4E8EC]">
                  <div className="flex items-start gap-3 text-sm text-[#1B2A4A]/60">
                    <i className="ri-shield-check-line text-[#81D8D0] text-lg flex-shrink-0 mt-0.5"></i>
                    <p className="leading-relaxed text-xs">
                      安全のため、すべてのやり取りはPawBridge内で行ってください。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Buttons */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#D4E8EC] p-4 shadow-lg z-40">
        {/* �安心保証バッジ */}
        <Link
          to="/guarantee"
          className="flex items-center justify-center gap-1.5 mb-2.5"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-shield-check-fill text-sm text-[#81D8D0]"></i>
          </div>
          <span className="text-xs text-[#1B2A4A]/55">全ての予約に安心保証が含まれています</span>
        </Link>
        <div className="flex gap-3">
          <button
            onClick={() => setShowChat(!showChat)}
            className="flex-1 px-4 py-3 bg-white border-2 border-[#1B2A4A] text-[#1B2A4A] text-sm font-semibold rounded-xl hover:bg-[#1B2A4A] hover:text-white transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-message-3-line text-lg"></i>
            メッセージ
          </button>

          <Link
            to={`/booking?sitterId=${sitter.id}`}
            className="flex-1 px-4 py-3 bg-[#81D8D0] text-white text-sm font-bold rounded-xl hover:bg-[#1B2A4A] transition-all flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <i className="ri-calendar-check-line text-lg"></i>
            今すぐ予約
          </Link>
        </div>
      </div>

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-none sm:rounded-2xl w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[80vh] flex flex-col shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-[#D4E8EC] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden">
                  <img src={sitter.profileImage} alt={sitter.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-[#1B2A4A]">{sitter.name}</h3>
                  <p className="text-xs sm:text-sm text-[#1B2A4A]/60">オンライン</p>
                </div>
              </div>
              <button 
                onClick={() => setShowChat(false)}
                className="w-10 h-10 flex items-center justify-center text-[#1B2A4A]/60 hover:text-[#1B2A4A] hover:bg-[#EEF4F7] rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {chatMessages.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <i className="ri-chat-3-line text-4xl sm:text-5xl text-[#D4E8EC] mb-4"></i>
                  <p className="text-sm sm:text-base text-[#1B2A4A]/60">メッセージを送信してください</p>
                  <div className="mt-6 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-xs sm:text-sm text-[#1B2A4A] text-left max-w-md mx-auto">
                    <div className="flex items-start gap-2">
                      <i className="ri-alert-line text-yellow-600 text-base sm:text-lg flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-yellow-800 mb-1">重要なお知らせ</p>
                        <p className="leading-relaxed">外部連絡先の共有は禁止されています。すべてのやり取りはPawBridge内で行ってください。</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                chatMessages.map((msg, index) => (
                  <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] sm:max-w-[70%] ${msg.sender === 'user' ? 'bg-[#81D8D0] text-white' : 'bg-[#EEF4F7] text-[#1B2A4A]'} rounded-2xl px-3 sm:px-4 py-2 sm:py-3`}>
                      <p className="text-xs sm:text-sm leading-relaxed">{msg.text}</p>
                      <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-white/70' : 'text-[#1B2A4A]/50'}`}>{msg.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-[#D4E8EC]">
              <div className="flex gap-2 sm:gap-3">
                <input 
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="メッセージを入力..."
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-[#D4E8EC] rounded-xl text-xs sm:text-sm outline-none focus:border-[#81D8D0] transition-colors"
                />
                <button 
                  onClick={handleSendMessage}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-[#81D8D0] text-white rounded-xl hover:bg-[#1B2A4A] transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill text-base sm:text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}